import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { UsersRound, Filter, Mail, Phone, Plus, UserPlus, Trash2, Copy, Settings, Briefcase, Eye, MoreHorizontal, FolderOpen } from 'lucide-react';
import { ProfileImageUpload, AttachmentsList } from '../components/FileUpload';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

// Default professional disciplines (clinical)
const DEFAULT_DISCIPLINES = [
  { id: 'occupational_therapy', name: 'Occupational Therapy (OT)', category: 'clinical' },
  { id: 'physiotherapy', name: 'Physiotherapy (PT)', category: 'clinical' },
  { id: 'speech_language_therapy', name: 'Speech & Language Therapy (SLT)', category: 'clinical' },
  { id: 'psychology', name: 'Psychology', category: 'clinical' },
  { id: 'special_education', name: 'Special Education', category: 'clinical' },
  { id: 'nutrition', name: 'Nutrition', category: 'clinical' }
];

const emptyStaffMember = {
  email: '',
  first_name: '',
  last_name: '',
  role: 'therapist',
  discipline: '',
  phone: '',
  job_title: '',
  start_date: '',
  qualifications: '',
  hcpc_number: ''
};

export default function StaffPage() {
  const { api, user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [addStaffDialogOpen, setAddStaffDialogOpen] = useState(false);
  const [staffForms, setStaffForms] = useState([{ ...emptyStaffMember }]);
  const [createdCredentials, setCreatedCredentials] = useState([]);
  
  // Super Admin check
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  
  // Custom professional types
  const [customProfTypes, setCustomProfTypes] = useState([]);
  const [profTypeDialogOpen, setProfTypeDialogOpen] = useState(false);
  const [profTypeForm, setProfTypeForm] = useState({
    name: '',
    category: 'support',
    description: '',
    requires_registration: false,
    registration_body: ''
  });
  
  // Staff view dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  useEffect(() => {
    fetchStaff();
    fetchCustomProfTypes();
  }, [roleFilter, disciplineFilter]);

  const fetchStaff = async () => {
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (disciplineFilter !== 'all') params.discipline = disciplineFilter;
      const response = await api.get('/staff', { params });
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomProfTypes = async () => {
    try {
      const response = await api.get('/hr/professional-types');
      setCustomProfTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch custom professional types:', error);
    }
  };

  const handleCreateProfType = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/professional-types', profTypeForm);
      toast.success(`Professional type "${profTypeForm.name}" created successfully`);
      setProfTypeDialogOpen(false);
      setProfTypeForm({ name: '', category: 'support', description: '', requires_registration: false, registration_body: '' });
      fetchCustomProfTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create professional type');
    }
  };

  const handleDeleteProfType = async (typeId, typeName) => {
    if (!window.confirm(`Are you sure you want to delete "${typeName}"?`)) return;
    try {
      await api.delete(`/hr/professional-types/${typeId}`);
      toast.success('Professional type deleted');
      fetchCustomProfTypes();
    } catch (error) {
      toast.error('Failed to delete professional type');
    }
  };

  // Delete staff member
  const handleDeleteStaff = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete "${staffName}"? This action cannot be undone and will remove all related records.`)) return;
    try {
      await api.delete(`/staff/${staffId}`);
      toast.success(`Staff member "${staffName}" deleted successfully`);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff member');
    }
  };

  // Combine default and custom disciplines for dropdown
  const allDisciplines = [
    ...DEFAULT_DISCIPLINES,
    ...customProfTypes.map(pt => ({ id: `custom_${pt.id}`, name: pt.name, category: pt.category }))
  ];

  const addAnotherStaff = () => {
    setStaffForms([...staffForms, { ...emptyStaffMember }]);
  };

  const removeStaffForm = (index) => {
    if (staffForms.length > 1) {
      setStaffForms(staffForms.filter((_, i) => i !== index));
    }
  };

  const updateStaffForm = (index, field, value) => {
    const newForms = [...staffForms];
    newForms[index][field] = value;
    setStaffForms(newForms);
  };

  const handleAddMultipleStaff = async (e) => {
    e.preventDefault();
    const credentials = [];
    let successCount = 0;
    
    for (const staffForm of staffForms) {
      if (!staffForm.email || !staffForm.first_name || !staffForm.last_name) {
        continue; // Skip incomplete entries
      }
      
      try {
        const response = await api.post('/staff', staffForm);
        credentials.push({
          name: `${staffForm.first_name} ${staffForm.last_name}`,
          email: staffForm.email,
          password: response.data.temporary_password
        });
        successCount++;
      } catch (error) {
        toast.error(`Failed to add ${staffForm.first_name} ${staffForm.last_name}: ${error.response?.data?.detail || 'Unknown error'}`);
      }
    }
    
    if (successCount > 0) {
      setCreatedCredentials(credentials);
      toast.success(`${successCount} staff member(s) created successfully!`);
      fetchStaff();
    }
  };

  const resetAndCloseDialog = () => {
    setAddStaffDialogOpen(false);
    setStaffForms([{ ...emptyStaffMember }]);
    setCreatedCredentials([]);
  };

  const copyCredentials = () => {
    const text = createdCredentials.map(c => 
      `${c.name}\nEmail: ${c.email}\nPassword: ${c.password}`
    ).join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied to clipboard');
  };

  const getRoleColor = (role) => {
    const colors = {
      director: 'bg-purple-100 text-purple-700',
      managing_director: 'bg-purple-200 text-purple-800',
      clinical_lead: 'bg-blue-100 text-blue-700',
      therapist: 'bg-teal-100 text-teal-700',
      rehab_assistant: 'bg-cyan-100 text-cyan-700',
      admin: 'bg-slate-100 text-slate-700',
      reception: 'bg-orange-100 text-orange-700',
      finance: 'bg-amber-100 text-amber-700',
      hr: 'bg-pink-100 text-pink-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  const getDisciplineLabel = (discipline) => {
    if (!discipline) return '-';
    
    // Check default disciplines
    const defaultLabels = {
      occupational_therapy: 'OT',
      physiotherapy: 'PT',
      speech_language_therapy: 'SLT',
      psychology: 'Psychology',
      special_education: 'Special Ed',
      nutrition: 'Nutrition'
    };
    
    if (defaultLabels[discipline]) return defaultLabels[discipline];
    
    // Check custom types
    if (discipline.startsWith('custom_')) {
      const customType = customProfTypes.find(pt => `custom_${pt.id}` === discipline);
      return customType?.name || discipline;
    }
    
    // Check if it's a custom type ID directly
    const customType = customProfTypes.find(pt => pt.id === discipline || pt.name.toLowerCase() === discipline.toLowerCase());
    if (customType) return customType.name;
    
    return discipline;
  };

  const openViewDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setViewDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Staff Directory</h1>
          <p className="text-slate-500 mt-1">Manage staff and professionals</p>
        </div>
        
        <div className="flex gap-2">
          {/* Create Professional Type Button */}
          {(user?.role === 'director' || user?.role === 'admin' || user?.role === 'hr') && (
            <Dialog open={profTypeDialogOpen} onOpenChange={setProfTypeDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="create-prof-type-btn">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Create Professional Type
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Professional Type</DialogTitle>
                  <p className="text-sm text-slate-500">Add custom professional categories like Driver, Nurse, Receptionist, etc.</p>
                </DialogHeader>
                <form onSubmit={handleCreateProfType} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Professional Type Name *</Label>
                    <Input 
                      value={profTypeForm.name} 
                      onChange={(e) => setProfTypeForm({...profTypeForm, name: e.target.value})} 
                      placeholder="e.g., Driver, Nurse, Receptionist, Care Assistant"
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={profTypeForm.category} onValueChange={(v) => setProfTypeForm({...profTypeForm, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clinical">Clinical</SelectItem>
                        <SelectItem value="administrative">Administrative</SelectItem>
                        <SelectItem value="support">Support Staff</SelectItem>
                        <SelectItem value="management">Management</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={profTypeForm.description} 
                      onChange={(e) => setProfTypeForm({...profTypeForm, description: e.target.value})} 
                      placeholder="Brief description of this professional type..."
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={profTypeForm.requires_registration}
                        onChange={(e) => setProfTypeForm({...profTypeForm, requires_registration: e.target.checked})}
                      />
                      <span className="text-sm">Requires Professional Registration</span>
                    </label>
                  </div>
                  {profTypeForm.requires_registration && (
                    <div className="space-y-2">
                      <Label>Registration Body</Label>
                      <Input 
                        value={profTypeForm.registration_body} 
                        onChange={(e) => setProfTypeForm({...profTypeForm, registration_body: e.target.value})} 
                        placeholder="e.g., NMC, GMC, HCPC"
                      />
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setProfTypeDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-[#1e3a5f]">Create Professional Type</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Add Professionals Button */}
        {(user?.role === 'director' || user?.role === 'admin' || user?.role === 'hr') && (
          <Dialog open={addStaffDialogOpen} onOpenChange={(open) => { if (!open) resetAndCloseDialog(); else setAddStaffDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="add-staff-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Professionals
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Professionals / Staff Members</DialogTitle>
                <p className="text-sm text-slate-500">Add one or multiple staff members at once</p>
              </DialogHeader>
              
              {createdCredentials.length > 0 ? (
                // Show credentials after creation
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-800 mb-3">Staff Members Created Successfully!</h3>
                    <p className="text-sm text-green-700 mb-4">Please save these credentials and share with the new staff members:</p>
                    
                    <div className="space-y-3">
                      {createdCredentials.map((cred, idx) => (
                        <div key={idx} className="bg-white p-3 rounded border border-green-200">
                          <p className="font-medium text-slate-800">{cred.name}</p>
                          <p className="text-sm text-slate-600">Email: {cred.email}</p>
                          <p className="text-sm text-slate-600">Temporary Password: <span className="font-mono bg-slate-100 px-1 rounded">{cred.password}</span></p>
                        </div>
                      ))}
                    </div>
                    
                    <Button onClick={copyCredentials} variant="outline" className="mt-4">
                      <Copy className="w-4 h-4 mr-2" />
                      Copy All Credentials
                    </Button>
                  </div>
                  
                  <DialogFooter>
                    <Button onClick={resetAndCloseDialog} className="bg-[#1e3a5f]">Done</Button>
                  </DialogFooter>
                </div>
              ) : (
                // Show form for adding staff
                <form onSubmit={handleAddMultipleStaff} className="space-y-4">
                  {staffForms.map((staffForm, index) => (
                    <Card key={index} className={`p-4 ${index > 0 ? 'border-2 border-dashed border-slate-300' : ''}`}>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold text-[#1e3a5f]">Professional #{index + 1}</h4>
                        {staffForms.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeStaffForm(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">First Name *</Label>
                          <Input 
                            value={staffForm.first_name} 
                            onChange={(e) => updateStaffForm(index, 'first_name', e.target.value)} 
                            required 
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Last Name *</Label>
                          <Input 
                            value={staffForm.last_name} 
                            onChange={(e) => updateStaffForm(index, 'last_name', e.target.value)} 
                            required 
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email *</Label>
                          <Input 
                            type="email"
                            value={staffForm.email} 
                            onChange={(e) => updateStaffForm(index, 'email', e.target.value)} 
                            required 
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Role *</Label>
                          <Select value={staffForm.role} onValueChange={(v) => updateStaffForm(index, 'role', v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Management */}
                              <SelectItem value="director">Director</SelectItem>
                              <SelectItem value="managing_director">Managing Director</SelectItem>
                              
                              {/* Clinical */}
                              <SelectItem value="clinical_lead">Clinical Lead</SelectItem>
                              <SelectItem value="therapist">Therapist</SelectItem>
                              <SelectItem value="rehab_assistant">Rehab Assistant</SelectItem>
                              
                              {/* Administration */}
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="reception">Reception</SelectItem>
                              
                              {/* Finance & HR */}
                              <SelectItem value="finance">Finance</SelectItem>
                              <SelectItem value="hr">HR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Discipline / Type</Label>
                          <Select value={staffForm.discipline} onValueChange={(v) => updateStaffForm(index, 'discipline', v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Clinical Disciplines */}
                              <SelectItem value="occupational_therapy">Occupational Therapy (OT)</SelectItem>
                              <SelectItem value="physiotherapy">Physiotherapy (PT)</SelectItem>
                              <SelectItem value="speech_language_therapy">Speech & Language (SLT)</SelectItem>
                              <SelectItem value="psychology">Psychology</SelectItem>
                              <SelectItem value="special_education">Special Education</SelectItem>
                              <SelectItem value="nutrition">Nutrition</SelectItem>
                              
                              {/* Custom Professional Types */}
                              {customProfTypes.length > 0 && customProfTypes.map(pt => (
                                <SelectItem key={pt.id} value={`custom_${pt.id}`}>
                                  {pt.name} ({pt.category})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Phone</Label>
                          <Input 
                            type="tel"
                            value={staffForm.phone} 
                            onChange={(e) => updateStaffForm(index, 'phone', e.target.value)} 
                            placeholder="+44..."
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Job Title</Label>
                          <Input 
                            value={staffForm.job_title} 
                            onChange={(e) => updateStaffForm(index, 'job_title', e.target.value)} 
                            placeholder="e.g., Senior OT"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Qualifications</Label>
                          <Input 
                            value={staffForm.qualifications} 
                            onChange={(e) => updateStaffForm(index, 'qualifications', e.target.value)} 
                            placeholder="BSc, MSc..."
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">HCPC Number</Label>
                          <Input 
                            value={staffForm.hcpc_number} 
                            onChange={(e) => updateStaffForm(index, 'hcpc_number', e.target.value)} 
                            placeholder="OT12345"
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  <Button type="button" variant="outline" onClick={addAnotherStaff} className="w-full border-dashed">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Professional
                  </Button>
                  
                  <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
                    <p><strong>Note:</strong> A temporary password (Welcome123!) will be assigned to each staff member. They should change it upon first login.</p>
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={resetAndCloseDialog}>Cancel</Button>
                    <Button type="submit" className="bg-[#1e3a5f]">
                      Create {staffForms.length} Professional{staffForms.length > 1 ? 's' : ''}
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Custom Professional Types Section */}
      {customProfTypes.length > 0 && (
        <Card className="card-icrcl">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#1e3a5f]">Custom Professional Types</CardTitle>
            <CardDescription>Professional types you've created for your organisation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {customProfTypes.map((pt) => (
                <div key={pt.id} className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-full">
                  <Briefcase className="w-4 h-4 text-[#1e3a5f]" />
                  <span className="text-sm font-medium">{pt.name}</span>
                  <Badge variant="outline" className="text-xs">{pt.category}</Badge>
                  {(user?.role === 'director' || user?.role === 'admin') && (
                    <button 
                      onClick={() => handleDeleteProfType(pt.id, pt.name)}
                      className="text-red-400 hover:text-red-600 ml-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="card-icrcl">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-5 h-5 text-slate-400" />
            <div className="w-48">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="director">Directors</SelectItem>
                  <SelectItem value="clinical_lead">Clinical Leads</SelectItem>
                  <SelectItem value="therapist">Therapists</SelectItem>
                  <SelectItem value="rehab_assistant">Rehab Assistants</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="reception">Reception</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="hr">HR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by discipline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Disciplines</SelectItem>
                  <SelectItem value="occupational_therapy">OT</SelectItem>
                  <SelectItem value="physiotherapy">PT</SelectItem>
                  <SelectItem value="speech_language_therapy">SLT</SelectItem>
                  <SelectItem value="psychology">Psychology</SelectItem>
                  <SelectItem value="special_education">Special Education</SelectItem>
                  <SelectItem value="nutrition">Nutrition</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Staff Table */}
      <Card className="card-icrcl">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : staff.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <UsersRound className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No staff found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-icrcl">
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Discipline</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((member) => (
                  <TableRow key={member.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1e3a5f] flex items-center justify-center text-white font-medium">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleColor(member.role)}>
                        {member.role?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {member.discipline ? (
                        <Badge variant="outline">
                          {getDisciplineLabel(member.discipline)}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {member.phone && (
                          <p className="text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {member.phone}
                          </p>
                        )}
                        <p className="text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {member.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={member.is_active ? 'badge-active' : 'badge-cancelled'}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewDialog(member)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openViewDialog(member)}>
                            <FolderOpen className="w-4 h-4 mr-2" />
                            Documents
                          </DropdownMenuItem>
                          {isSuperAdmin && member.id !== user?.id && member.email?.toLowerCase() !== 'admin@icrcl.co.uk' && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteStaff(member.id, `${member.first_name} ${member.last_name}`)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Staff
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Staff View/Edit Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedStaff && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#1e3a5f]">
                  {selectedStaff.first_name} {selectedStaff.last_name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Profile Image */}
                <div className="flex flex-col items-center">
                  <ProfileImageUpload
                    staffId={selectedStaff.id}
                    currentImage={selectedStaff.profile_image}
                    onUploadComplete={fetchStaff}
                  />
                  <Badge className={getRoleColor(selectedStaff.role)} style={{ marginTop: '1rem' }}>
                    {selectedStaff.role?.replace(/_/g, ' ')}
                  </Badge>
                </div>
                
                {/* Staff Details */}
                <div className="md:col-span-2 space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Email</p>
                      <p className="font-medium">{selectedStaff.email}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Phone</p>
                      <p className="font-medium">{selectedStaff.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Job Title</p>
                      <p className="font-medium">{selectedStaff.job_title || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Discipline</p>
                      <p className="font-medium">{getDisciplineLabel(selectedStaff.discipline)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">HCPC Number</p>
                      <p className="font-medium">{selectedStaff.hcpc_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Status</p>
                      <Badge className={selectedStaff.is_active ? 'badge-active' : 'badge-cancelled'}>
                        {selectedStaff.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  {selectedStaff.qualifications && (
                    <div>
                      <p className="text-slate-500 text-sm">Qualifications</p>
                      <p className="font-medium">{selectedStaff.qualifications}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* HR Documents */}
              <div className="mt-6">
                <AttachmentsList
                  entityType="staff"
                  entityId={selectedStaff.id}
                  title="HR Documents (Passport, DBS, Certificates)"
                  showUploadButton={user?.role === 'director' || user?.role === 'admin' || user?.role === 'hr' || user?.id === selectedStaff.id}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
